import {
  makeStyles,
  Typography,
  Box,
  Button,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MemoryIcon from '@material-ui/icons/Memory';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import SpeedIcon from '@material-ui/icons/Speed';
import { Page, Content } from '@backstage/core-components';

const DASHBOARDS = [
  {
    id: 'gravitee-apim',
    title: 'Gravitee APIM',
    subtitle: 'High Level View',
    description: 'API Management metrics, traffic patterns, and gateway health across all environments.',
    url: 'https://app.datadoghq.com/dashboard/aii-mtk-4ud/gravitee-apim-high-level-view',
    icon: TrendingUpIcon,
    color: '#6366f1',
  },
  {
    id: 'hydra-costs',
    title: 'Hydra Costs',
    subtitle: 'Forecasted Annual',
    description: 'Forecasted annual cost breakdown and optimization opportunities for Hydra applications.',
    url: 'https://app.datadoghq.com/dashboard/9mg-q49-cpu/hydra-application-forecasted-annual-cost',
    icon: AttachMoneyIcon,
    color: '#10b981',
  },
  {
    id: 'dvde-http-metrics',
    title: 'DVDE HTTP',
    subtitle: 'Metrics',
    description: 'Request rates, latency percentiles, and error budgets for DVDE services.',
    url: 'https://app.datadoghq.com/dashboard/9pg-2vh-2is/dvde-http-metrics',
    icon: SpeedIcon,
    color: '#f59e0b',
  },
  {
    id: 'gpu-efficiencies',
    title: 'GPU Efficiencies',
    subtitle: 'Usage',
    description: 'GPU node utilization, scheduling efficiency, and capacity planning across Hydra clusters.',
    url: 'https://app.datadoghq.com/dashboard/7hz-usw-jcc/hydra-gpu-node-efficiencies-usage',
    icon: MemoryIcon,
    color: '#ec4899',
  },
  {
    id: 'vcp-auth-prod',
    title: 'VCP Auth',
    subtitle: 'Production',
    description: 'Authentication service health, token issuance rates, and latency for production.',
    url: 'https://app.datadoghq.com/dashboard/h9y-ry6-q29/vcp-auth---prod',
    icon: VpnKeyIcon,
    color: '#0ea5e9',
  },
];

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  return {
    root: {
      padding: '32px 40px',
      maxWidth: 1200,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: 20,
    },
    card: {
      position: 'relative' as const,
      borderRadius: 18,
      padding: '28px 26px 24px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 16,
      background: isDark ? 'rgba(255,255,255,0.035)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
      transition: 'all 0.28s cubic-bezier(0.2, 0, 0, 1)',
      cursor: 'default',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: isDark
          ? '0 20px 48px -12px rgba(0,0,0,0.5)'
          : '0 20px 48px -12px rgba(0,0,0,0.1)',
        borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
      },
    },
    iconContainer: {
      width: 44,
      height: 44,
      borderRadius: 13,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardTitle: {
      fontSize: 17,
      fontWeight: 700,
      color: isDark ? '#ffffff' : '#1a1a2e',
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
    },
    cardSubtitle: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const,
      opacity: 0.6,
    },
    cardDescription: {
      fontSize: 13.5,
      lineHeight: 1.6,
      color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.6)',
      flex: 1,
    },
    button: {
      borderRadius: 12,
      textTransform: 'none' as const,
      fontWeight: 600,
      fontSize: 13,
      padding: '9px 18px',
      boxShadow: 'none',
      letterSpacing: '-0.01em',
      '&:hover': {
        boxShadow: 'none',
      },
    },
  };
});

export const DatadogDashboardPage = () => {
  const classes = useStyles();

  return (
    <Page themeId="tool">
      <Content>
        <Box className={classes.root}>
          <Box className={classes.grid}>
            {DASHBOARDS.map(d => {
              const Icon = d.icon;
              return (
                <Box key={d.id} className={classes.card}>
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box
                      className={classes.iconContainer}
                      style={{ background: `${d.color}18` }}
                    >
                      <Icon style={{ color: d.color, fontSize: 22 }} />
                    </Box>
                  </Box>
                  <Box>
                    <Typography className={classes.cardSubtitle} style={{ color: d.color, marginBottom: 4 }}>
                      {d.subtitle}
                    </Typography>
                    <Typography className={classes.cardTitle}>
                      {d.title}
                    </Typography>
                  </Box>
                  <Typography className={classes.cardDescription}>
                    {d.description}
                  </Typography>
                  <Box>
                    <Button
                      className={classes.button}
                      variant="contained"
                      size="small"
                      endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: d.color,
                        color: '#ffffff',
                      }}
                    >
                      Open Dashboard
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
